import React from 'react';
import { LFC } from '../../../Legacy';
import { Colors } from '../../../ui';
import { RenderBounds, RenderBox, RenderGraffiti, RenderInner, usePose } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { JiraSprint } from './JiraSprint';
import { JiraSprintDateTextSettings } from './fields/JiraSprintDateField';
import { JiraSprintGoalTextSettings } from './fields/JiraSprintGoalField';
import { JiraSprintNameTextSettings } from './fields/JiraSprintNameField';

const RenderDesign: LFC<{ object: JiraSprint }> = ({ object }) => {
    return <>
        <RenderBox
            inset="0"
            border={`solid 1px ${Colors.border}`}
            borderBottom={`solid 4px ${Colors.border}`}
            borderRadius="6px"
            background="white"
        />
    </>;
};

const RenderSprintName: LFC<{ object: JiraSprint }> = ({ object }) => {
    const { beingEdited } = usePose(object);
    const { layout, record } = object;

    if (beingEdited) return null;

    return <RenderText
        bounds={layout.name}
        settings={JiraSprintNameTextSettings}
        value={record?.name || 'Sprint Name'}
    />;
};

const RenderSprintDates: LFC<{ object: JiraSprint }> = ({ object }) => {
    const { layout, record } = object;

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString();
        }
        catch {
            return dateString;
        }
    };

    return <>
        <RenderText
            bounds={layout.fromDate}
            settings={JiraSprintDateTextSettings}
            value={formatDate(record?.startDate) || 'Start Date'}
        />
        <RenderText
            bounds={layout.toDate}
            settings={JiraSprintDateTextSettings}
            value={formatDate(record?.endDate) || 'End Date'}
        />
    </>;
};

const RenderSprintGoal: LFC<{ object: JiraSprint }> = ({ object }) => {
    const { beingEdited } = usePose(object);
    const { layout, record } = object;

    if (beingEdited) return null;

    return <>
        <RenderBox
            {...layout.goal.toCSS()}
            border={`solid 1px ${Colors.border}`}
            borderRadius="4px"
            background={Colors.gray50}
        />
        <RenderText
            bounds={layout.goal}
            settings={JiraSprintGoalTextSettings}
            value={record?.goal || 'Sprint Goal'}
        />
    </>;
};

export const JiraSprintVisual: LFC<{ object: JiraSprint }> = ({ object, children }) => {
    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign object={object} />
                <RenderGraffiti object={object} />
                <RenderSprintName object={object} />
                <RenderSprintDates object={object} />
                <RenderSprintGoal object={object} />
            </RenderInner>
        </RenderBounds >
    </>;
};
